import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Clock, Landmark, Pickaxe, FileText, Gavel, Banknote, ShieldAlert,
  Sparkles, Filter,
} from "lucide-react";

/**
 * Executive Timeline — presentation-only. Consumes existing endpoints
 * (applications, disputes, revenue ledger, risk register) and merges them
 * into a chronological national feed. Does not modify any operational record.
 */

const KIND_META = {
  application:  { icon: FileText,     accent: "#6BB88A", label: "Application" },
  dispute:      { icon: Gavel,        accent: "#E67E7E", label: "Dispute" },
  revenue:      { icon: Banknote,     accent: "#4FA3D1", label: "Revenue" },
  cabinet_item: { icon: Sparkles,     accent: "#7B2FBE", label: "Cabinet item" },
  risk:         { icon: ShieldAlert,  accent: "#E67E7E", label: "Risk" },
};

const useFeed = () => {
  const apps = useQuery({ queryKey: ["tl-apps"],      queryFn: async () => (await api.get("/applications?limit=25")).data });
  const disp = useQuery({ queryKey: ["tl-disputes"], queryFn: async () => (await api.get("/disputes?limit=25")).data });
  const cab  = useQuery({ queryKey: ["tl-cab"],      queryFn: async () => (await api.get("/cadastre360/gsros/cabinet-command-centre")).data });
  const risk = useQuery({ queryKey: ["tl-risk"],     queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data });

  const items = useMemo(() => {
    const rows = [];
    (Array.isArray(apps.data) ? apps.data : apps.data?.items || []).slice(0, 25).forEach((a) => {
      rows.push({
        kind: "application",
        id: a.id || a.application_number,
        title: `${a.application_type || "Application"} · ${a.applicant_name || ""}`,
        detail: `${a.status || ""} · ${a.province || ""}`,
        at: a.created_at || a.updated_at,
      });
    });
    (Array.isArray(disp.data) ? disp.data : disp.data?.items || []).slice(0, 20).forEach((d) => {
      rows.push({
        kind: "dispute",
        id: d.id || d.dispute_number,
        title: `Dispute · ${d.dispute_type || ""}`,
        detail: `${d.status || ""} · ${d.province || ""}`,
        at: d.created_at || d.updated_at,
      });
    });
    (cab.data?.top_priority_items || []).forEach((c) => {
      rows.push({
        kind: "cabinet_item",
        id: c.id,
        title: c.detail,
        detail: `${c.portfolio} · urgency ${c.urgency_score}`,
        at: c.raised_at,
      });
    });
    (risk.data?.risks || []).forEach((r) => {
      rows.push({
        kind: "risk",
        id: r.id,
        title: `${r.risk}`,
        detail: `${r.category} · ${r.impact} impact · ${r.owner}`,
        at: r.last_reviewed,
      });
    });
    return rows.filter((r) => r.at).sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [apps.data, disp.data, cab.data, risk.data]);

  return { items, loading: apps.isLoading || disp.isLoading || cab.isLoading || risk.isLoading };
};

export const ExecutiveTimeline = () => {
  const { items, loading } = useFeed();
  const [kind, setKind] = useState("all");
  const filtered = kind === "all" ? items : items.filter((i) => i.kind === kind);
  const kinds = ["all", "application", "dispute", "cabinet_item", "risk"];

  return (
    <div className="p-6 space-y-4" data-testid="page-executive-timeline">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#4FA3D160", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(79,163,209,0.2))" }}>
        <Clock size={32} className="text-[#4FA3D1] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#4FA3D1]">National Executive Timeline</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">One chronological view</h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-3xl">
            Applications, disputes, cabinet items and risks in one national feed. Presentation-only — every row deep-links to the module that owns it.
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-3 flex flex-wrap items-center gap-2" style={{ background: "rgba(11,53,88,0.35)" }}>
        <Filter size={12} className="text-[#7C93A5]" />
        {kinds.map((k) => (
          <button key={k} onClick={() => setKind(k)}
                  data-testid={"timeline-filter-" + k}
                  className="rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] border"
                  style={{
                    background: k === kind ? "#4FA3D130" : "transparent",
                    color: k === kind ? "#F6F8FA" : "#B3C2CC",
                    borderColor: k === kind ? "#4FA3D1" : "rgba(255,255,255,0.08)",
                  }}>
            {k === "all" ? "All" : KIND_META[k]?.label}
          </button>
        ))}
        <div className="ml-auto font-mono text-[10px] text-[#7C93A5]">
          {loading ? "Loading…" : `${filtered.length} entries`}
        </div>
      </div>

      <div className="rounded-sm border border-white/10" style={{ background: "rgba(11,53,88,0.25)" }}>
        {filtered.map((it, i) => {
          const meta = KIND_META[it.kind] || KIND_META.application;
          const Icon = meta.icon;
          return (
            <div key={it.kind + "-" + it.id + "-" + i} className="flex items-start gap-3 px-3 py-2.5 border-b border-white/5">
              <div className="rounded-sm p-2" style={{ background: meta.accent + "20" }}>
                <Icon size={14} style={{ color: meta.accent }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase" style={{ background: meta.accent + "25", color: meta.accent }}>
                    {meta.label}
                  </span>
                  <span className="text-[10.5px] font-mono text-[#7C93A5]">{(it.at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="text-[12.5px] text-[#F6F8FA] font-medium mt-0.5 truncate">{it.title}</div>
                <div className="text-[10.5px] font-mono text-[#7C93A5] truncate">{it.detail}</div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-[11.5px] text-[#7C93A5] italic">No events for this filter.</div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveTimeline;
